import { GridState } from "@ag-grid-community/core";
import { Alert, AlertDescription, AlertIcon } from "@chakra-ui/alert";
import { WarningIcon } from "@chakra-ui/icons";
import { Button, useDisclosure } from "@chakra-ui/react";
import { MaybernTransactionsAssistantModal } from "features/ai/MaybernTransactionsAssistantModal";
import { CalculationActions } from "features/transactions/TransactionsViewTable/CalculationActions/CalculationActions";
import { useCalculationActions } from "features/transactions/TransactionsViewTable/CalculationActions/hooks";
import { useTableViewActions } from "features/transactions/TransactionsViewTable/useTableActions";
import { useTableStateAsFormValues } from "features/transactions/TransactionsViewTable/useTableAsFormValues";
import { useTransactionTableColumnsSuspense } from "gen/endpoints/transactions/transactions";
import { FeatureFlagNameEnum } from "gen/model/featureFlagNameEnum";
import { TableViewReadData } from "gen/model/tableViewReadData";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import {
  DataTable,
  DataTableRef,
} from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useDataTableServerSideSource } from "shared/components/table/data/DataTableServerSideSource";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { compressString } from "shared/utils/CompressionUtils";

export const TransactionsViewTable = ({
  view,
}: {
  view?: TableViewReadData;
}) => {
  const isDraft = !view;
  const { data: columnData } = useTransactionTableColumnsSuspense();
  const ref = useRef<DataTableRef>(null);
  const isMaybernAssistantActive = useFeatureFlagActive(
    FeatureFlagNameEnum.maybern_assistant,
  );
  const [aiState, setAIState] = useState<GridState>();

  const {
    canSave,
    invalidTableState,
    initialState,
    tableKey,
    onTableStateUpdated,
    syncTableStateWithCurrentView,
    shouldShowWarning,
    getTableData,
  } = useTableStateAsFormValues({
    view,
    ref,
    aiState,
  });

  const getViewState = useCallback(async () => {
    const state = ref.current?.tableRef?.api.getState();
    const str = await compressString(JSON.stringify(state));
    return str;
  }, []);

  const actions = useTableViewActions({
    view,
    isDraft,
    syncTableStateWithCurrentView,
    canSave,
    getViewState,
  });
  const { t } = useTranslation(["views"]);

  const {
    fundFamilyIds,
    setFundFamilyIds,
    transactionCalculationId,
    setTransactionCalculationId,
    onFilterChanged,
    sideBar,
  } = useCalculationActions(ref, initialState);

  const datasource = useDataTableServerSideSource(getTableData);
  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();

  return (
    <>
      {isModalOpen && (
        <MaybernTransactionsAssistantModal
          onCloseComplete={closeModal}
          setAIState={setAIState}
        />
      )}
      <TableBox
        isFullWidth={true}
        boxProps={{
          sx: {
            borderTopWidth: 0,
            marginX: 0,
          },
        }}
      >
        <TableBoxHeader height="55px">
          <TableBoxTitle>
            {view?.name || t("views:viewTypes.allTransactions")}
          </TableBoxTitle>

          <DataTableBoxActions
            showExport={!invalidTableState}
            getExportParams={() => undefined}
            suppressReset={true}
            showPivot={true}
          >
            {actions}
            {/* For now, don't show the AI button while in a view. Need to fix state management first. */}
            {!view && isMaybernAssistantActive && (
              <Button size="xs" colorScheme="tertiary" onClick={openModal}>
                {t("ai:transactions.ask")}
              </Button>
            )}
          </DataTableBoxActions>
        </TableBoxHeader>
        <CalculationActions
          initialState={initialState}
          fundFamilyIds={fundFamilyIds}
          setFundFamilyIds={setFundFamilyIds}
          transactionCalculationId={transactionCalculationId}
          setTransactionCalculationId={setTransactionCalculationId}
        />
        <TableBoxBody>
          <DataTable
            key={tableKey}
            data={columnData}
            serverSideDatasource={datasource}
            initialState={initialState}
            suppressColumnVirtualisation={false}
            onStateUpdated={onTableStateUpdated}
            onFilterChanged={onFilterChanged}
            sideBar={sideBar}
            ref={ref}
          />
        </TableBoxBody>
      </TableBox>
      {shouldShowWarning && (
        <Alert width="unset" status="warning" mt="16px" mx="16px">
          <AlertIcon>
            <WarningIcon />
          </AlertIcon>
          <AlertDescription>{t("views:errors.viewEmpty")}</AlertDescription>
        </Alert>
      )}
    </>
  );
};
